import React from "react";
import values from "lodash/values";
import pick from "lodash/pickBy";
import moment from "moment";
import twix from "twix";
import { Link } from "react-router";
import { Checkbox, IconButton } from "material-ui";
import { Table, Tr, Td, Sort } from "reactable";
import UserStore from "../../stores/UserStore";

import { makeEventDateRangeLabel } from "../../util";
import TableRefreshIndicator from "./table-refresh-indicator.jsx";

class EventTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showScanCounts: false,
      showNonTicketed: true
    };

    this._onShowScanCountsCheck = this._onShowScanCountsCheck.bind(this);
    this._onShowNonTicketedCheck = this._onShowNonTicketedCheck.bind(this);
    this._refresh = this._refresh.bind(this);
    this._makeRowForEvent = this._makeRowForEvent.bind(this);
  }

  _onShowScanCountsCheck(e) {
    let show = !this.state.showScanCounts;
    this.setState({
      showScanCounts: show
    });
    this.props.onShowScanCountsCheck(show);
  }

  _onShowNonTicketedCheck(e) {
    this.setState({
      showNonTicketed: !this.state.showNonTicketed
    });
  }

  _refresh() {
    this.props.onRefresh();
  }

  getStyles() {
    return {};
  }

  _makeRowForEvent(event, isPast) {
    let soldTickets = event.quantity_sold,
      percentSold = (soldTickets / event.quantity) * 100 || 0,
      percentScanned = (event.quantity_scanned / soldTickets) * 100 || 0;

    return (
      <Tr key={event.id}>
        <Td column="Name">{event.name}</Td>
        <Td column="Venue" value={event.venue && event.venue.name}>
          {event.venue && event.venue.name ? event.venue.name : ""}
        </Td>
        <Td column="Date" value={event.start_time || "-"}>
          {makeEventDateRangeLabel(event)}
        </Td>
        <Td column="Tickets Sold" value={soldTickets}>
          {event.unlimited
            ? `${soldTickets}/∞`
            : `${soldTickets}/${event.quantity}`}
        </Td>
        <Td column="% Sold" value={event.unlimited ? -1 : percentSold}>
          {event.unlimited ? "n/a" : `${percentSold.toFixed(1)}%`}
        </Td>
        {this.state.showScanCounts ? (
          <Td column="Tickets Scanned" value={event.quantity_scanned}>
            {`${event.quantity_scanned}/${soldTickets}`}
          </Td>
        ) : null}
        {this.state.showScanCounts ? (
          <Td column="% Scanned" value={soldTickets ? percentScanned : -1}>
            {soldTickets ? `${percentScanned.toFixed(1)}%` : "n/a"}
          </Td>
        ) : null}

        <Td column="Actions">
          <div>
            {UserStore.hasRole("film_preview") &&
            isPast ? null : UserStore.hasPermission("view_virtual_event") ||
              UserStore.hasPermission("view_physical_event") ? (
              <Link
                to={{
                  pathname: `/event_buckets/${event.event_bucket}/${
                    this.props.virtual ? "virtual_festival/" : ""
                  }events/${event.id}`,
                  state: { fromListing: true }
                }}
              >
                View
              </Link>
            ) : null}
            &nbsp;
            {UserStore.hasPermission("edit_virtual_event") ||
            UserStore.hasPermission("edit_physical_event") ? (
              <Link
                to={{
                  pathname: `/event_buckets/${event.event_bucket}/${
                    this.props.virtual ? "virtual_festival/" : ""
                  }events/${event.id}/edit`,
                  state: { fromListing: true }
                }}
              >
                Edit
              </Link>
            ) : null}
          </div>
        </Td>
      </Tr>
    );
  }

  render() {
    let styles = this.getStyles(),
      { showNonTicketed } = this.state,
      events = values(
        pick(this.props.events, e => {
          if (!showNonTicketed && e.ticket_buckets.length === 0) {
            return false;
          }
          if (e.name === "Unnamed Event" && !e.ticket_buckets.length) {
            // Filter out created but never edited "accident" events
            return false;
          }
          return true;
        })
      );

    let upcomingEvents = events.filter(
        e =>
          !e.is_dated ||
          !e.start_time ||
          !e.end_time ||
          e.end_time === e.start_time ||
          new Date(e.end_time) > Date.now()
      ),
      pastEvents = events.filter(e => upcomingEvents.indexOf(e) === -1);

    let tableProps = {
      className: "bordered striped responsive-table",
      sortable: [
        "Date",
        "Name",
        "Venue",
        "Tickets Sold",
        "% Sold",
        "Tickets Scanned",
        "% Scanned"
      ],
      defaultSort: this.props.virtual
        ? { column: "Name", direction: "asc" }
        : { column: "Date", direction: "asc" },
      columns: ["Name"]
        .concat(
          this.props.virtual
            ? [{ key: "Date", label: "Availability" }]
            : ["Venue", "Date"]
        )
        .concat(["Tickets Sold", "% Sold"])
        .concat(
          this.state.showScanCounts ? ["Tickets Scanned", "% Scanned"] : []
        ),
      filterable: ["Name"]
    };

    let tablePropsPast = Object.assign({}, tableProps);
    if (
      UserStore.hasPermission("view_virtual_event") ||
      UserStore.hasPermission("edit_virtual_event") ||
      UserStore.hasPermission("view_physical_event") ||
      UserStore.hasPermission("edit_physical_event")
    ) {
      tableProps.columns = tableProps.columns.concat(["Actions"]);
    }

    if (
      UserStore.hasPermission("edit_virtual_event") ||
      UserStore.hasPermission("view_physical_event") ||
      UserStore.hasPermission("edit_physical_event") ||
      (UserStore.hasPermission("view_virtual_event") &&
        !UserStore.hasRole("film_preview"))
    ) {
      tablePropsPast.columns = tablePropsPast.columns.concat(["Actions"]);
    }

    return (
      <div>
        <style>{`
          .reactable-filterer input {
            width: 250px;
          }
        `}</style>
        <div style={styles.config}>
          <div style={{ float: "left", width: 300 }}>
            <Checkbox
              checked={this.state.showScanCounts}
              label="Show ticket scan counts"
              onCheck={e => this._onShowScanCountsCheck(e, !e.target.checked)}
            />
            <Checkbox
              checked={this.state.showNonTicketed}
              label={`Show non-ticketed ${
                this.props.virtual ? "screenings" : "events"
              }`}
              onCheck={e => this._onShowNonTicketedCheck(e, !e.target.checked)}
            />
          </div>
          <TableRefreshIndicator
            isRefreshing={this.props.isRefreshing}
            onRefresh={this._refresh}
          />
        </div>
        {upcomingEvents.length ? (
          <Table
            {...tableProps}
            filterPlaceholder={`Type to filter upcoming ${
              this.props.virtual ? "screenings" : "events"
            }...`}
          >
            {upcomingEvents.map(ue => this._makeRowForEvent(ue, false))}
          </Table>
        ) : null}
        {this.props.createButton && upcomingEvents.length ? (
          <div style={{ marginTop: "1em" }}>{this.props.createButton}</div>
        ) : null}
        {!upcomingEvents.length || pastEvents.length ? (
          <div>
            {upcomingEvents.length ? (
              <h2
                style={Object.assign(
                  { textAlign: "center", marginTop: "1.5em" },
                  this.props.headerStyle
                )}
              >
                Past events
              </h2>
            ) : null}
            <Table
              {...tablePropsPast}
              filterPlaceholder={`Type to filter ${
                this.props.virtual ? "past screenings" : "events"
              }...`}
            >
              {pastEvents.map(pe => this._makeRowForEvent(pe, true))}
            </Table>
          </div>
        ) : null}
        {pastEvents.length === 0 && upcomingEvents.length === 0 ? (
          <div className="empty-table-message">
            No {this.props.virtual ? "virtual screenings" : "events"} yet.{" "}
            <Link
              to={`/event_buckets/${this.props.eventBucket}/events/new${
                this.props.virtual ? "?virtual=true" : ""
              }`}
            >
              Create a new {this.props.virtual ? "virtual screening" : "event"}.
            </Link>
          </div>
        ) : null}
        {this.props.createButton && !upcomingEvents.length ? (
          <div style={{ marginTop: "1em" }}>{this.props.createButton}</div>
        ) : null}
      </div>
    );
  }
}

export default EventTable;



// WEBPACK FOOTER //
// ./src/app/components/partials/event-table.jsx